import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import FinancialTicker from './FinancialTicker';

const WEATHER_LOCATIONS = [
  { name: 'Mt. Pleasant, SC', lat: 32.8747, lon: -79.7961 },
  { name: 'Hanover, MD', lat: 39.1714, lon: -76.7233 },
];

const WEATHER_CODES = {
  0: { icon: '☀️', label: 'Clear' },
  1: { icon: '🌤', label: 'Mostly Clear' },
  2: { icon: '⛅', label: 'Partly Cloudy' },
  3: { icon: '☁️', label: 'Overcast' },
  45: { icon: '🌫', label: 'Fog' },
  48: { icon: '🌫', label: 'Rime Fog' },
  51: { icon: '🌦', label: 'Light Drizzle' },
  53: { icon: '🌦', label: 'Drizzle' },
  55: { icon: '🌧', label: 'Heavy Drizzle' },
  61: { icon: '🌧', label: 'Light Rain' },
  63: { icon: '🌧', label: 'Rain' },
  65: { icon: '🌧', label: 'Heavy Rain' },
  66: { icon: '🌨', label: 'Freezing Rain' },
  67: { icon: '🌨', label: 'Heavy Freezing Rain' },
  71: { icon: '❄️', label: 'Light Snow' },
  73: { icon: '❄️', label: 'Snow' },
  75: { icon: '❄️', label: 'Heavy Snow' },
  77: { icon: '🌨', label: 'Snow Grains' },
  80: { icon: '🌦', label: 'Light Showers' },
  81: { icon: '🌧', label: 'Showers' },
  82: { icon: '⛈', label: 'Heavy Showers' },
  85: { icon: '🌨', label: 'Snow Showers' },
  86: { icon: '🌨', label: 'Heavy Snow Showers' },
  95: { icon: '⛈', label: 'Thunderstorm' },
  96: { icon: '⛈', label: 'Thunderstorm w/ Hail' },
  99: { icon: '⛈', label: 'Thunderstorm w/ Heavy Hail' },
};

const CODE_LINKS = [
  { section: '103', title: 'Exclusion of interest on state and local bonds', url: 'https://www.law.cornell.edu/uscode/text/26/103' },
  { section: '141', title: 'Private activity bond; qualified bond', url: 'https://www.law.cornell.edu/uscode/text/26/141' },
  { section: '142', title: 'Exempt facility bond', url: 'https://www.law.cornell.edu/uscode/text/26/142' },
  { section: '143', title: 'Mortgage revenue bonds', url: 'https://www.law.cornell.edu/uscode/text/26/143' },
  { section: '144', title: 'Qualified small issue bond; qualified redevelopment bond', url: 'https://www.law.cornell.edu/uscode/text/26/144' },
  { section: '145', title: 'Qualified 501(c)(3) bond', url: 'https://www.law.cornell.edu/uscode/text/26/145' },
  { section: '146', title: 'Volume cap', url: 'https://www.law.cornell.edu/uscode/text/26/146' },
  { section: '147', title: 'Other requirements applicable to certain private activity bonds', url: 'https://www.law.cornell.edu/uscode/text/26/147' },
  { section: '148', title: 'Arbitrage', url: 'https://www.law.cornell.edu/uscode/text/26/148' },
  { section: '149', title: 'Bonds must be registered to be tax-exempt', url: 'https://www.law.cornell.edu/uscode/text/26/149' },
  { section: '150', title: 'Definition and special rules', url: 'https://www.law.cornell.edu/uscode/text/26/150' },
];

const REGS_LINKS = [
  { header: '103' },
  { reg: '§ 1.103-1', title: 'Interest on state and local bonds', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.103-1' },
  { header: '141' },
  { reg: '§ 1.141-0', title: 'Table of contents', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.141-0' },
  { reg: '§ 1.141-1', title: 'Definitions and rules of general application', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.141-1' },
  { reg: '§ 1.141-2', title: 'Private activity bond restrictions', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.141-2' },
  { reg: '§ 1.141-3', title: 'Definition of private business use', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.141-3' },
  { reg: '§ 1.141-4', title: 'Private security or payment test', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.141-4' },
  { reg: '§ 1.141-5', title: 'Private loan financing test', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.141-5' },
  { reg: '§ 1.141-6', title: 'Allocation rules', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.141-6' },
  { reg: '§ 1.141-7', title: 'Special rules for output facilities', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.141-7' },
  { reg: '§ 1.141-8', title: '$15 million limitation for output facilities', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.141-8' },
  { reg: '§ 1.141-9', title: 'Unrelated or disproportionate use', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.141-9' },
  { reg: '§ 1.141-10', title: 'Coordination with volume cap', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.141-10' },
  { reg: '§ 1.141-11', title: 'Acquisition of nongovernmental output property', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.141-11' },
  { reg: '§ 1.141-12', title: 'Remedial actions', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.141-12' },
  { reg: '§ 1.141-13', title: 'Refunding issues', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.141-13' },
  { header: '142' },
  { reg: '§ 1.142-0', title: 'Table of contents', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.142-0' },
  { reg: '§ 1.142-2', title: 'Remedial actions for exempt facility bonds', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.142-2' },
  { reg: '§ 1.142(a)(5)-1', title: 'Solid waste disposal facilities', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.142(a)(5)-1' },
  { reg: '§ 1.142(f)(4)-1', title: 'Election to terminate tax-exempt bond financing', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.142(f)(4)-1' },
  { header: '143' },
  { reg: '§ 1.143(g)-1', title: 'Requirements related to arbitrage (mortgage revenue bonds)', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.143(g)-1' },
  { header: '144' },
  { reg: '§ 1.144-0', title: 'Table of contents', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.144-0' },
  { reg: '§ 1.144-1', title: 'Qualified small issue, student loan, and redevelopment bonds', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.144-1' },
  { reg: '§ 1.144-2', title: 'Remedial actions', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.144-2' },
  { header: '145' },
  { reg: '§ 1.145-0', title: 'Table of contents', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.145-0' },
  { reg: '§ 1.145-1', title: 'Qualified 501(c)(3) bonds', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.145-1' },
  { reg: '§ 1.145-2', title: 'Application of private activity bond regulations', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.145-2' },
  { header: '147' },
  { reg: '§ 1.147-0', title: 'Table of contents', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.147-0' },
  { reg: '§ 1.147-1', title: 'Other requirements applicable to certain private activity bonds', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.147-1' },
  { reg: '§ 1.147-2', title: 'Remedial actions', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.147-2' },
  { reg: '§ 1.147(b)-1', title: 'Bond maturity limitation; treatment of working capital', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.147(b)-1' },
  { reg: '§ 1.147(f)-1', title: 'Public approval of private activity bonds (TEFRA)', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.147(f)-1' },
  { header: '148' },
  { reg: '§ 1.148-0', title: 'Scope and table of contents', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.148-0' },
  { reg: '§ 1.148-1', title: 'Definitions and elections', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.148-1' },
  { reg: '§ 1.148-2', title: 'General arbitrage yield restriction rules', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.148-2' },
  { reg: '§ 1.148-3', title: 'General arbitrage rebate rules', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.148-3' },
  { reg: '§ 1.148-4', title: 'Yield on an issue of bonds', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.148-4' },
  { reg: '§ 1.148-5', title: 'Yield and valuation of investments', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.148-5' },
  { reg: '§ 1.148-6', title: 'General allocation and accounting rules', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.148-6' },
  { reg: '§ 1.148-7', title: 'Spending exceptions to rebate', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.148-7' },
  { reg: '§ 1.148-8', title: 'Small issuer exception to rebate', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.148-8' },
  { reg: '§ 1.148-9', title: 'Arbitrage rules for refunding issues', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.148-9' },
  { reg: '§ 1.148-10', title: 'Anti-abuse rules', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.148-10' },
  { reg: '§ 1.148-11', title: 'Effective/applicability dates', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.148-11' },
  { header: '149' },
  { reg: '§ 1.149(b)-1', title: 'Federally guaranteed bonds', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.149(b)-1' },
  { reg: '§ 1.149(d)-1', title: 'Limitations on advance refundings', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.149(d)-1' },
  { reg: '§ 1.149(e)-1', title: 'Information reporting requirements', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.149(e)-1' },
  { reg: '§ 1.149(g)-1', title: 'Hedge bonds', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.149(g)-1' },
  { header: '150' },
  { reg: '§ 1.150-1', title: 'Definitions', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.150-1' },
  { reg: '§ 1.150-2', title: 'Proceeds of bonds used for reimbursement', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.150-2' },
  { reg: '§ 1.150-3', title: 'Reissuance of state or local bonds (effective December 30, 2024)', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.150-3' },
  { reg: '§ 1.150-4', title: 'Change in use of facilities financed with tax-exempt private activity bonds', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.150-4' },
  { reg: '§ 1.150-5', title: 'Filing notices and elections', url: 'https://www.ecfr.gov/current/title-26/chapter-I/subchapter-A/part-1/section-1.150-5' },
];

const dropdownBaseStyle = {
  position: 'fixed',
  background: 'var(--bg-primary, #fff)', border: '1px solid var(--border-color, #ddd)',
  borderRadius: '6px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
  minWidth: '380px', maxHeight: '400px', overflowY: 'auto',
  zIndex: 99999, padding: '4px 0',
};

// Portal dropdown: renders at document.body so overflow:hidden parents can't clip it
const PortalDropdown = ({ anchorRef, show, children, portalRef }) => {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (show && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
  }, [show, anchorRef]);
  if (!show) return null;
  return ReactDOM.createPortal(
    <div ref={portalRef} style={{ ...dropdownBaseStyle, top: pos.top, left: pos.left }}>{children}</div>,
    document.body
  );
};

const dropdownItemStyle = {
  display: 'flex', gap: '8px', alignItems: 'baseline',
  padding: '7px 14px', fontSize: '12px', color: 'var(--text-primary, #333)',
  textDecoration: 'none', cursor: 'pointer', transition: 'background 0.1s',
};

const sectionHeaderStyle = {
  padding: '6px 14px 3px', fontSize: '10px', fontWeight: 700,
  color: 'var(--text-secondary, #999)', textTransform: 'uppercase',
  letterSpacing: '0.05em', borderTop: '1px solid var(--border-color, #eee)',
  marginTop: '2px',
};

const TopBar = ({
  selectedModel,
  setSelectedModel,
  activeTab,
  setActiveTab,
  isDarkTheme,
  toggleTheme,
  customPrompt,
  promptInputValue,
  setPromptInputValue,
  showPromptEditor,
  setShowPromptEditor,
  updateCustomPrompt,
  resetToDefaultPrompt,
  showDocViewer,
  setShowDocViewer,
}) => {
  const [showSettings, setShowSettings] = useState(false);
  const [showDropboxConfirm, setShowDropboxConfirm] = useState(false);
  const [dropboxRunning, setDropboxRunning] = useState(false);
  const [weather, setWeather] = useState({});
  const [showCodeDropdown, setShowCodeDropdown] = useState(false);
  const [showRegsDropdown, setShowRegsDropdown] = useState(false);
  const [show8038Dropdown, setShow8038Dropdown] = useState(false);
  const [corpusHealth, setCorpusHealth] = useState(null);
  const [corpusLoading, setCorpusLoading] = useState(false);
  const [showCorpusModal, setShowCorpusModal] = useState(false);

  const fetchCorpusHealth = async () => {
    setCorpusLoading(true);
    setShowCorpusModal(true);
    try {
      const resp = await fetch('/api/corpus/health');
      if (resp.ok) {
        const data = await resp.json();
        setCorpusHealth(data);
      } else {
        setCorpusHealth({ error: `HTTP ${resp.status}` });
      }
    } catch (e) {
      setCorpusHealth({ error: e.message });
    }
    setCorpusLoading(false);
  };
  const [showCalcDropdown, setShowCalcDropdown] = useState(false);
  const [showTaxCertDropdown, setShowTaxCertDropdown] = useState(false);
  const [showAuthoritiesDropdown, setShowAuthoritiesDropdown] = useState(false);
  const [authoritiesView, setAuthoritiesView] = useState('main'); // 'main' | 'codeRegs'
  const settingsRef = useRef(null);
  const codeRef = useRef(null);
  const regsRef = useRef(null);
  const form8038Ref = useRef(null);
  const calcRef = useRef(null);
  const taxCertRef = useRef(null);
  const authoritiesRef = useRef(null);
  const codePortalRef = useRef(null);
  const form8038PortalRef = useRef(null);
  const calcPortalRef = useRef(null);
  const regsPortalRef = useRef(null);
  const taxCertPortalRef = useRef(null);
  const authoritiesPortalRef = useRef(null);

  const closeAllDropdowns = () => {
    setShow8038Dropdown(false);
    setShowCodeDropdown(false);
    setShowRegsDropdown(false);
    setShowCalcDropdown(false);
    setShowTaxCertDropdown(false);
    setShowAuthoritiesDropdown(false);
  };

  // Fetch weather for both locations on mount and every 30 minutes
  useEffect(() => {
    const fetchWeather = async () => {
      for (const loc of WEATHER_LOCATIONS) {
        try {
          const url = `https://api.open-meteo.com/v1/forecast?latitude=${loc.lat}&longitude=${loc.lon}&current=temperature_2m,weather_code,wind_speed_10m,relative_humidity_2m&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=America%2FNew_York`;
          const res = await fetch(url);
          if (res.ok) {
            const data = await res.json();
            const current = data.current;
            setWeather(prev => ({
              ...prev,
              [loc.name]: {
                temp: Math.round(current.temperature_2m),
                code: current.weather_code,
                wind: Math.round(current.wind_speed_10m),
                humidity: current.relative_humidity_2m,
              }
            }));
          }
        } catch (err) {
          console.warn(`Weather fetch failed for ${loc.name}:`, err);
        }
      }
    };
    fetchWeather();
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setShowSettings(false);
      }
      if (codeRef.current && !codeRef.current.contains(e.target) &&
          !(codePortalRef.current && codePortalRef.current.contains(e.target))) {
        setShowCodeDropdown(false);
      }
      if (regsRef.current && !regsRef.current.contains(e.target) &&
          !(regsPortalRef.current && regsPortalRef.current.contains(e.target))) {
        setShowRegsDropdown(false);
      }
      if (form8038Ref.current && !form8038Ref.current.contains(e.target) &&
          !(form8038PortalRef.current && form8038PortalRef.current.contains(e.target))) {
        setShow8038Dropdown(false);
      }
      if (calcRef.current && !calcRef.current.contains(e.target) &&
          !(calcPortalRef.current && calcPortalRef.current.contains(e.target))) {
        setShowCalcDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDropboxProcess = async () => {
    setDropboxRunning(true);
    setShowDropboxConfirm(false);
    try {
      const startTime = Date.now();
      const response = await fetch('/api/run_unified_dropbox', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      const elapsed = Math.round((Date.now() - startTime) / 1000);
      if (result.success) {
        alert(`Dropbox processing completed in ${elapsed}s.`);
      } else {
        alert(`Processing failed: ${result.error}`);
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    } finally {
      setDropboxRunning(false);
    }
  };

  return (
    <div className="ribbon">
      {/* ── Primary Row ──────────────────────────────────── */}
      <div className="ribbon-primary">
        {/* Left: Brand + New Chat */}
        <div className="ribbon-left">
          <span className="ribbon-brand">PublicFinanceTax</span>
        </div>

        {/* Right: Settings */}
        <div className="ribbon-right">
          {/* Settings gear */}
          <div className="ribbon-settings-wrapper" ref={settingsRef}>
            <button
              className={`ribbon-icon-btn ${showSettings ? 'active' : ''}`}
              onClick={() => setShowSettings(!showSettings)}
              title="Settings"
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"/>
              </svg>
            </button>

            {showSettings && (
              <div className="ribbon-dropdown">
                <div className="ribbon-dropdown-section">
                  <div className="ribbon-dropdown-label">Appearance</div>
                  <label className="ribbon-toggle-row">
                    <span>Dark Theme</span>
                    <input type="checkbox" checked={isDarkTheme} onChange={toggleTheme} />
                  </label>
                </div>

                <div className="ribbon-dropdown-section">
                  <div className="ribbon-dropdown-label">Custom Prompt</div>
                  <button
                    onClick={() => setShowPromptEditor(!showPromptEditor)}
                    className="ribbon-dropdown-btn"
                  >
                    {showPromptEditor ? 'Hide Editor' : 'Edit System Prompt'}
                  </button>
                  {showPromptEditor && (
                    <div style={{ marginTop: '8px' }}>
                      <textarea
                        value={promptInputValue}
                        onChange={(e) => setPromptInputValue(e.target.value)}
                        placeholder="Enter custom system prompt..."
                        className="ribbon-prompt-textarea"
                      />
                      <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                        <button onClick={updateCustomPrompt} className="ribbon-prompt-btn save">Save</button>
                        <button onClick={resetToDefaultPrompt} className="ribbon-prompt-btn reset">Reset</button>
                      </div>
                      {customPrompt && (
                        <div className="ribbon-prompt-preview">
                          Active: {customPrompt.length > 60 ? customPrompt.substring(0, 60) + '…' : customPrompt}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="ribbon-dropdown-section">
                  <div className="ribbon-dropdown-label">System</div>
                  {!showDropboxConfirm ? (
                    <button
                      onClick={() => setShowDropboxConfirm(true)}
                      className="ribbon-dropdown-btn"
                      disabled={dropboxRunning}
                    >
                      {dropboxRunning ? 'Processing…' : 'Run Dropbox Sync'}
                    </button>
                  ) : (
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>This may take 10–15 min.</span>
                      <button onClick={handleDropboxProcess} className="ribbon-prompt-btn save" style={{ fontSize: '11px' }}>Go</button>
                      <button onClick={() => setShowDropboxConfirm(false)} className="ribbon-prompt-btn reset" style={{ fontSize: '11px' }}>Cancel</button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Weather + Ticker Strip ────────────────────────── */}
      <div className="ribbon-weather">
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexShrink: 0 }}>
          {WEATHER_LOCATIONS.map(loc => {
            const w = weather[loc.name];
            const info = w ? (WEATHER_CODES[w.code] || { icon: '🌡', label: '' }) : null;
            return (
              <div key={loc.name} className="weather-item">
                {w ? (
                  <>
                    <span className="weather-icon">{info.icon}</span>
                    <span className="weather-location">{loc.name}</span>
                    <span className="weather-temp">{w.temp}°F</span>
                    <span className="weather-detail">{info.label}</span>
                    <span className="weather-detail">Wind {w.wind} mph</span>
                  </>
                ) : (
                  <>
                    <span className="weather-location">{loc.name}</span>
                    <span className="weather-detail">Loading…</span>
                  </>
                )}
              </div>
            );
          })}
        </div>
        <FinancialTicker inline />
      </div>

      {/* ── Consolidated Tools Row ─────────────────────── */}
      <div className="ribbon-tools" style={{ width: '100%', height: 'auto', minHeight: '36px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: '6px', width: '100%', padding: '4px 0' }}>
          <button
            className={`ribbon-pill ${activeTab === 'txreview' ? 'on' : ''}`}
            onClick={() => setActiveTab('txreview')}
            title="Upload deal documents for structured tax compliance analysis"
          >
            Tx Review
          </button>

          <a
            className="ribbon-pill"
            href="https://compliance.publicfinancetax.com"
            target="_blank"
            rel="noopener noreferrer"
            title="Post-issuance compliance monitoring — opens in new tab"
          >
            Compliance
          </a>

          <div ref={form8038Ref} style={{ position: 'relative' }}>
            <button
              className={`ribbon-pill ${activeTab === '8038g' || activeTab === '8038' || show8038Dropdown ? 'on' : ''}`}
              onClick={() => { setShow8038Dropdown(!show8038Dropdown); setShowCodeDropdown(false); setShowRegsDropdown(false); setShowCalcDropdown(false); }}
            >
              8038s ▾
            </button>
            <PortalDropdown anchorRef={form8038Ref} show={show8038Dropdown} portalRef={form8038PortalRef}>
              <button style={{ ...dropdownItemStyle, border: 'none', background: 'transparent', width: '100%', textAlign: 'left' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary, #f5f5f5)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                onClick={() => { setActiveTab('8038'); setShow8038Dropdown(false); }}
              >
                Form 8038
              </button>
              <button style={{ ...dropdownItemStyle, border: 'none', background: 'transparent', width: '100%', textAlign: 'left' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary, #f5f5f5)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                onClick={() => { setActiveTab('8038g'); setShow8038Dropdown(false); }}
              >
                Form 8038-G
              </button>
            </PortalDropdown>
          </div>

          <div ref={taxCertRef} style={{ position: 'relative' }}>
            <button
              className={`ribbon-pill ${activeTab === 'taxCert' || activeTab === 'languageLib' || showTaxCertDropdown ? 'on' : ''}`}
              onClick={() => {
                const next = !showTaxCertDropdown;
                closeAllDropdowns();
                setShowTaxCertDropdown(next);
              }}
            >
              Tax Cert ▾
            </button>
            <PortalDropdown anchorRef={taxCertRef} show={showTaxCertDropdown} portalRef={taxCertPortalRef}>
              <button style={{ ...dropdownItemStyle, border: 'none', background: 'transparent', width: '100%', textAlign: 'left' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary, #f5f5f5)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                onClick={() => { setActiveTab('taxCert'); setShowTaxCertDropdown(false); }}
              >
                Tax Certificate
              </button>
              <button style={{ ...dropdownItemStyle, border: 'none', background: 'transparent', width: '100%', textAlign: 'left' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary, #f5f5f5)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                onClick={() => { setActiveTab('languageLib'); setShowTaxCertDropdown(false); }}
              >
                Language Library
              </button>
            </PortalDropdown>
          </div>

          <button
            className={`ribbon-pill ${activeTab === 'disclosure' ? 'on' : ''}`}
            onClick={() => setActiveTab('disclosure')}
          >
            Disclosure
          </button>
          <button
            className={`ribbon-pill ${activeTab === 'complianceManual' ? 'on' : ''}`}
            onClick={() => setActiveTab('complianceManual')}
          >
            Compliance Manual
          </button>

          <div ref={calcRef} style={{ position: 'relative' }}>
            <button
              className={`ribbon-pill ${activeTab === 'yieldCalc' || activeTab === 'loanYieldCalc' || activeTab === 'wamCalc' || showCalcDropdown ? 'on' : ''}`}
              onClick={() => { setShowCalcDropdown(!showCalcDropdown); setShowCodeDropdown(false); setShowRegsDropdown(false); setShow8038Dropdown(false); }}
            >
              Yield/WAM ▾
            </button>
            <PortalDropdown anchorRef={calcRef} show={showCalcDropdown} portalRef={calcPortalRef}>
              <button style={{ ...dropdownItemStyle, border: 'none', background: 'transparent', width: '100%', textAlign: 'left' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary, #f5f5f5)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                onClick={() => { setActiveTab('yieldCalc'); setShowCalcDropdown(false); }}
              >
                Bond Yield Calculator
              </button>
              <button style={{ ...dropdownItemStyle, border: 'none', background: 'transparent', width: '100%', textAlign: 'left' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary, #f5f5f5)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                onClick={() => { setActiveTab('loanYieldCalc'); setShowCalcDropdown(false); }}
              >
                Loan Yield Calculator
              </button>
              <button style={{ ...dropdownItemStyle, border: 'none', background: 'transparent', width: '100%', textAlign: 'left' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary, #f5f5f5)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                onClick={() => { setActiveTab('wamCalc'); setShowCalcDropdown(false); }}
              >
                WAM Calculator
              </button>
            </PortalDropdown>
          </div>

          <div ref={authoritiesRef} style={{ position: 'relative' }}>
            <button
              className={`ribbon-pill ${showDocViewer || showAuthoritiesDropdown ? 'on' : ''}`}
              onClick={() => {
                const next = !showAuthoritiesDropdown;
                closeAllDropdowns();
                setAuthoritiesView('main');
                setShowAuthoritiesDropdown(next);
              }}
              title="Search authorities, browse Code and Regulations, or check corpus health"
            >
              Authorities ▾
            </button>
            <PortalDropdown anchorRef={authoritiesRef} show={showAuthoritiesDropdown} portalRef={authoritiesPortalRef}>
              {authoritiesView === 'main' && (
                <>
                  <button style={{ ...dropdownItemStyle, border: 'none', background: 'transparent', width: '100%', textAlign: 'left' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary, #f5f5f5)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    onClick={() => {
                      if (setShowDocViewer) setShowDocViewer(!showDocViewer);
                      setShowAuthoritiesDropdown(false);
                    }}
                  >
                    Search
                  </button>
                  <button style={{ ...dropdownItemStyle, border: 'none', background: 'transparent', width: '100%', textAlign: 'left' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary, #f5f5f5)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    onClick={() => setAuthoritiesView('code')}
                  >
                    Code ▸
                  </button>
                  <button style={{ ...dropdownItemStyle, border: 'none', background: 'transparent', width: '100%', textAlign: 'left' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary, #f5f5f5)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    onClick={() => setAuthoritiesView('regs')}
                  >
                    Regulations ▸
                  </button>
                </>
              )}
              {authoritiesView === 'code' && (
                <>
                  <button style={{ ...dropdownItemStyle, border: 'none', background: 'transparent', width: '100%', textAlign: 'left', color: 'var(--text-secondary, #666)', fontWeight: 600 }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary, #f5f5f5)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    onClick={() => setAuthoritiesView('main')}
                  >
                    ← Back
                  </button>
                  <div style={sectionHeaderStyle}>IRC (Cornell LII)</div>
                  {CODE_LINKS.map(item => (
                    <a key={'code-' + item.section} href={item.url} target="_blank" rel="noopener noreferrer" style={dropdownItemStyle}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary, #f5f5f5)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ fontWeight: 600, minWidth: '48px' }}>§ {item.section}</span>
                      <span style={{ color: 'var(--text-secondary, #666)' }}>{item.title}</span>
                    </a>
                  ))}
                </>
              )}
              {authoritiesView === 'regs' && (
                <>
                  <button style={{ ...dropdownItemStyle, border: 'none', background: 'transparent', width: '100%', textAlign: 'left', color: 'var(--text-secondary, #666)', fontWeight: 600 }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary, #f5f5f5)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    onClick={() => setAuthoritiesView('main')}
                  >
                    ← Back
                  </button>
                  <div style={sectionHeaderStyle}>Treasury Regs (eCFR)</div>
                  {REGS_LINKS.map((item, i) => (
                    item.header ? (
                      <div key={'rh-' + item.header + i} style={sectionHeaderStyle}>§ {item.header}</div>
                    ) : (
                      <a key={'reg-' + item.reg + i} href={item.url} target="_blank" rel="noopener noreferrer" style={dropdownItemStyle}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary, #f5f5f5)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <span style={{ color: 'var(--text-secondary, #666)' }}>{item.reg} — {item.title}</span>
                      </a>
                    )
                  ))}
                </>
              )}
            </PortalDropdown>
          </div>

        </div>
      </div>

      {showCorpusModal && ReactDOM.createPortal(
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setShowCorpusModal(false)}>
          <div style={{
            background: 'var(--bg-primary, #fff)', borderRadius: '8px', padding: '24px',
            maxWidth: '600px', width: '90%', maxHeight: '80vh', overflow: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)', color: 'var(--text-primary, #333)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>Corpus Health</h3>
              <button onClick={() => setShowCorpusModal(false)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: 'var(--text-primary)' }}>X</button>
            </div>
            {corpusLoading ? (
              <p>Running health check...</p>
            ) : corpusHealth?.error ? (
              <p style={{ color: '#d32f2f' }}>Error: {corpusHealth.error}</p>
            ) : corpusHealth ? (
              <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                <div style={{ padding: '8px', borderRadius: '4px', marginBottom: '12px',
                  background: corpusHealth.overall === 'pass' ? '#e8f5e9' : '#fff3e0',
                  color: corpusHealth.overall === 'pass' ? '#2e7d32' : '#e65100',
                  fontWeight: 600,
                }}>
                  Overall: {corpusHealth.overall?.toUpperCase()} — {corpusHealth.authorities?.passed}/{corpusHealth.authorities?.total} authorities
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <strong>Critical Authorities</strong>
                  <div style={{ marginTop: '4px' }}>
                    {corpusHealth.authorities?.results?.map((a, i) => (
                      <div key={i} style={{ display: 'flex', gap: '8px', padding: '2px 0' }}>
                        <span>{a.status === 'pass' ? '\u2705' : a.status === 'warn' ? '\u26A0\uFE0F' : '\u274C'}</span>
                        <span>{a.authority}</span>
                        <span style={{ color: '#888', marginLeft: 'auto' }}>{a.chunks} chunks</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <strong>Pinned Rules:</strong> {corpusHealth.pinned_rules?.count} rules
                  {corpusHealth.pinned_rules?.status === 'pass' ? ' \u2705' : ' \u26A0\uFE0F'}
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <strong>Corpus Size:</strong> {corpusHealth.corpus_size?.total?.toLocaleString()} docs
                  ({corpusHealth.corpus_size?.deviation_pct}% from baseline)
                  {corpusHealth.corpus_size?.status === 'pass' ? ' \u2705' : ' \u26A0\uFE0F'}
                </div>

                <div style={{ marginBottom: '8px' }}>
                  <strong>Critic Flags (7d):</strong> {corpusHealth.critic_flags_7d?.total_flags} flags, {corpusHealth.critic_flags_7d?.approved} approved
                </div>

                <div style={{ color: '#888', fontSize: '11px', marginTop: '12px' }}>
                  Last check: {corpusHealth.timestamp}
                </div>
              </div>
            ) : null}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default TopBar;
